import { useState } from 'react';
import { ChevronDown, MessageSquare } from 'lucide-react';

export interface EditLogEntry {
    action: string;
    userName: string;
    comment?: string;
    date: string;
}

export interface EditLogProps {
    editLog: EditLogEntry[];
    hidePreviewOnMobile?: boolean;
    title?: string;
    actionLabels?: Record<string, string>;
    dotColors?: Record<string, string>;
    showMoreLabel?: (remaining: number) => string;
    hideLabel?: string;
}

const DEFAULT_DOT_COLORS: Record<string, string> = {
    created: 'bg-blue-500',
    completed: 'bg-emerald-500',
    deleted: 'bg-red-500',
    edited: 'bg-muted-foreground/40',
    variables_changed: 'bg-amber-500',
};

const DEFAULT_ACTION_LABELS: Record<string, string> = {
    created: 'Created',
    edited: 'Edited',
    completed: 'Completed',
    deleted: 'Deleted',
    variables_changed: 'Variables changed',
};

function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

function formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
    });
}

export function EditLog({
    editLog,
    hidePreviewOnMobile = false,
    title = 'History',
    actionLabels,
    dotColors,
    showMoreLabel,
    hideLabel,
}: EditLogProps) {
    const [expanded, setExpanded] = useState(false);

    if (!editLog || editLog.length === 0) return null;

    const resolvedDotColors = { ...DEFAULT_DOT_COLORS, ...dotColors };
    const resolvedLabels = { ...DEFAULT_ACTION_LABELS, ...actionLabels };

    const sorted = [...editLog].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    const hasMore = sorted.length > 3;
    const visible = expanded ? sorted : sorted.slice(0, 3);
    const hideEntriesByDefault = hidePreviewOnMobile && !expanded;

    return (
        <div className="sticky top-8">
            <h4 className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider mb-3">
                {title}
            </h4>
            <div className="relative">
                <div className="absolute left-[3px] top-1.5 bottom-1.5 w-px bg-border/50" />

                <div
                    className={`space-y-3 ${
                        hideEntriesByDefault ? 'hidden lg:block' : ''
                    }`}
                >
                    {visible.map((entry, i) => (
                        <div key={i} className="relative pl-5">
                            <div
                                className={`absolute left-0 top-1.5 h-[7px] w-[7px] rounded-full ${
                                    resolvedDotColors[entry.action] ||
                                    resolvedDotColors.edited
                                }`}
                            />
                            <div className="text-xs">
                                <span className="text-foreground/80 font-medium">
                                    {resolvedLabels[entry.action] || entry.action}
                                </span>
                                <p className="text-muted-foreground/60 mt-0.5">
                                    {entry.userName}
                                </p>
                                <p className="text-muted-foreground/40">
                                    {formatDate(entry.date)} ·{' '}
                                    {formatTime(entry.date)}
                                </p>
                                {entry.comment && (
                                    <p className="flex items-start gap-1 text-muted-foreground/60 mt-1">
                                        <MessageSquare className="h-3 w-3 mt-0.5 shrink-0" />
                                        <span>{entry.comment}</span>
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {hasMore && (
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="flex items-center gap-1 text-xs text-muted-foreground/60 hover:text-muted-foreground mt-3 transition-colors"
                >
                    <ChevronDown
                        className={`h-3 w-3 transition-transform ${
                            expanded ? 'rotate-180' : ''
                        }`}
                    />
                    {expanded
                        ? (hideLabel ?? 'Show less')
                        : (showMoreLabel?.(sorted.length - 3) ?? `Show ${sorted.length - 3} more`)}
                </button>
            )}
        </div>
    );
}
